// NOTE Do not import like that it seems that then they are constants and the state is shared
// import { state, getters, actions, mutations } from '@shared/store/abstract-form.store';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { bpcApi } from '@core/services/bpc-api.service';
import BPCApiRouteHelper from '@core/helpers/bpc-api-route.helper';
import { PosSession } from '@pos/interfaces/pos-session';
import store from '@app/store';
import { Invoice } from '@accounting/interfaces/invoice';
import { Order } from '@accounting/interfaces/order';
import { Article } from '@accounting/interfaces/article';
import { appConfig } from '@/config/app-config';
import router from '@app/router';
import AuthorizationService from '@auth/services/authorization.service';

//  We can extends abstract state getters actions and muttations
//  For example:

const paramsForOpenTickets = (state, getters) => {
  const data = { params: {} };
  const config = store.getters['config/entityConfig']('invoice');
  if (config && config.selects && config.selects['open_tickets']) {
    const selectConfig = config.selects['open_tickets'];
    data.params['columns'] = selectConfig.fields ? selectConfig.fields : null;
    data.params['relationFields'] = selectConfig.relationFields
      ? selectConfig.relationFields
      : null;
    data.params['computedFields'] = selectConfig.computedFields
      ? selectConfig.computedFields
      : null;
    data.params['withRelations'] = selectConfig.withRelations ? selectConfig.withRelations : null;
  }
  data.params['pos_session_id'] = getters.openPosSessionId;
  data.params['status'] = ['to_pay', 'paid'];
  data.params['invoice_type'] = 'ticket';
  data.params['per_page'] = -1;
  data.params['in_pos_checkout'] = 1;
  return data;
};

const bpcRouterHelper = new BPCApiRouteHelper();
const $authorizationService = new AuthorizationService();

const state = {
  openPosSession: null,
  showDrawer: false,
  loading: false,
  loadingTickets: false,
  loadingCurrentTicket: false,
  changingOrdersOfTicket: false,
  currentTicketId: null,
  openTickets: [],
  orderItems: [],
  orderItemsToProcess: [],
  posExpanded: false,
  posInSidepane: false,
  showResumeSessionDrawer: false,
  showCloseSessionDrawer: false,
  modalsOpened: false,
  articleToAdd: null,
  currentPosBoxId: null,
  isPrinting: false,
};

const getters = {
  isPrinting: (state): boolean => {
    return state.isPrinting;
  },
  openPosSession: (state): PosSession => {
    return state.openPosSession || null;
  },
  openPosSessionId: (state, getters): number => {
    if (getters.openPosSession) {
      return getters.openPosSession?.id;
    }
    return null;
  },
  showDrawer: (state): boolean => {
    return state.showDrawer;
  },
  loading: (state): boolean => {
    return state.loading;
  },
  loadingTickets: (state): boolean => {
    return state.loadingTickets;
  },
  changingOrdersOfTicket: (state): boolean => {
    return state.changingOrdersOfTicket;
  },
  currentTicket: (state, getters): Invoice => {
    return getters.openTickets.find(ticket => {
      return ticket.id == getters.currentTicketId;
    });
  },
  currentTicketId: (state): number => {
    return state.currentTicketId;
  },
  currentTicketBalance: (state, getters): number => {
    return getters.currentTicket?.balance || 0;
  },
  openTickets: (state): Invoice[] => {
    return state.openTickets || [];
  },
  orderItems: (state): [] => {
    return state.orderItems || [];
  },
  orderItemsToProcess: (state): [] => {
    return state.orderItemsToProcess || [];
  },
  posExpanded: (state): boolean => {
    return state.posExpanded;
  },
  posInSidepane: (state): boolean => {
    return state.posInSidepane;
  },
  showResumeSessionDrawer: (state): boolean => {
    return state.showResumeSessionDrawer;
  },
  showCloseSessionDrawer: (state): boolean => {
    return state.showCloseSessionDrawer;
  },
  modalsOpened: (state): boolean => {
    return state.modalsOpened;
  },
  articleToAdd: (state): Article => {
    return state.articleToAdd;
  },
  currentPosBoxId: (state, getters): number => {
    if (getters.openPosSession) {
      return getters.openPosSession?.pos_box_id;
    }
    return null;
  },
  currentPosBox: (state, getters): number => {
    if (getters.openPosSession) {
      return getters.openPosSession?.pos_box;
    }
    return null;
  },
};
const actions = {
  getOpenPosSession({ dispatch, commit }): void {
    commit('startLoading');
    const tentant = store.getters['tenant/selectedTenant'];
    const profile = store.getters['auth/profile']({ tenantId: tentant.id });
    bpcApi
      .get(bpcRouterHelper.getEntityPath('pos-sessions'), {
        params: { status: 'opened', profile_id: profile ? profile.id : null },
      })
      .subscribe(
        (res: BPCGenericApiResponse) => {
          if (res.data[0]) {
            commit('storeOpenPosSession', res.data[0]);
            dispatch('getOpenTickets');
          } else if ($authorizationService.hasPermission('reopen_pos_sessions')) {
            dispatch('getReopenedPosSession');
          }
        },
        error => {
          console.log(error);
          commit('stopLoading');
        },
      );
  },
  getReopenedPosSession({ dispatch, commit }): void {
    commit('startLoading');
    const tentant = store.getters['tenant/selectedTenant'];
    const profile = store.getters['auth/profile']({ tenantId: tentant.id });
    bpcApi
      .get(bpcRouterHelper.getEntityPath('pos-sessions'), {
        params: { status: 'reopened', reviewer_profile_id: profile ? profile.id : null },
      })
      .subscribe(
        (res: BPCGenericApiResponse) => {
          if (res.data[0]) {
            commit('storeOpenPosSession', res.data[0]);
            dispatch('getOpenTickets');
          }
        },
        error => {
          commit('stopLoading');
        },
      );
  },
  getOpenTickets({ state, getters, commit }): Promise<never> {
    commit('startLoadingTickets');
    return new Promise((resolve, reject) => {
      const data = paramsForOpenTickets(state, getters);
      bpcApi.get(bpcRouterHelper.getEntityPath('invoice'), data).subscribe(
        (res: BPCGenericApiResponse) => {
          commit('storeOpenTickets', res.data);
          commit('stopLoadingTickets');
          resolve(res.data);
        },
        error => {
          commit('stopLoadingTickets');
          reject(error);
        },
      );
    });
  },
  reloadCurrentTicket({ state, getters, commit }): Promise<never> {
    return new Promise((resolve, reject) => {
      const invoiceId = getters.currentTicketId;
      commit('startLoadingCurrentTicket');
      const url = `${bpcRouterHelper.getEntityPath('invoice')}/${invoiceId}`;
      bpcApi.get(url).subscribe(
        (res: BPCGenericApiResponse) => {
          commit('replaceCurrentTicket', res.data);
          commit('stopLoadingCurrentTicket');
          resolve(res.data);
        },
        error => {
          commit('stopLoadingCurrentTicket');
          reject(error);
        },
      );
    });
  },
  addOrdersToTicketInvoice: async (
    { dispatch, commit, getters },
    data: {
      invoice_id: number;
      contact_id: number;
      pos_session_id: number;
      sale_day_id: number | null;
      ordersToCheckout: Order[];
      action?: string;
    },
  ): Promise<void> => {
    commit('startChangingOrdersOfTicket');

    // Adapt to create invoice function
    data['orders'] = data.ordersToCheckout.map(order => {
      return order.id;
    });
    data['online_sale_session_id'] = data.sale_day_id || null;
    // FIXME: BC-4930 This doesn't work in all the cases, only for a single enrollment from the row table.
    // Dirty fix to maintain the same behavior and avoid breaking the other cases
    // const order_item_id = data.ordersToCheckout[0].order_items[0].id;
    const order_item_id = data?.ordersToCheckout?.[0]?.order_items?.[0]?.id || null;
    const lang = store.getters['auth/userLang'] || 'src';
    const apiRoute = `${bpcRouterHelper.getEntityPath('invoice', lang)}/pos/add-orders`;
    const res = await bpcApi.post(apiRoute, data).toPromise();
    const currentTicket = res.data;
    let query = undefined;
    if (data.action) {
      query = {
        action: data.action,
        orderItemId: order_item_id,
      };
    }

    // Get open tickets and set the current one.
    await dispatch('getOpenTickets');
    commit('storeCurrentTicketId', currentTicket.id);
    commit('stopChangingOrdersOfTicket');
    if (store.getters['centerSettings/usePosV2'] && appConfig.modules.activatePosV2) {
      await router.push({
        name: 'pos-page',
        params: {
          initialPosSessionId: `${getters.openPosSessionId}`,
          initialTicketId: `${currentTicket.id}`,
        },
        query: query,
      });
    } else {
      if (!getters.showDrawer) {
        commit('toggleDrawer');
      }
    }
  },
  addArticleToTicketInvoice: async (
    { state, dispatch, commit, getters },
    data: {
      invoice_id: number;
      pos_session_id: number;
      article_id: number;
    },
  ): Promise<void> => {
    commit('startChangingOrdersOfTicket');

    const lang = store.getters['auth/userLang'] || 'src';
    const apiRoute = `${bpcRouterHelper.getEntityPath('invoice', lang)}/pos/add-article`;
    // const config = paramsForOpenTickets(state, getters);
    const res = await bpcApi.post(apiRoute, data).toPromise();
    // Update the current ticket.
    const index = getters.openTickets.findIndex(ticket => ticket.id == res['ticket']?.id);
    const allTickets = state.openTickets;

    allTickets[index] = res['ticket'];
    commit('storeOpenTickets', allTickets);
    // Refresh the current ticket for reactivity
    commit('clearCurrentTicket');
    commit('storeOrderItemsToProcess', res['orderItems']['data']);
    commit('stopChangingOrdersOfTicket');

    commit('storeCurrentTicketId', res['ticket']?.id);
  },
  addOrderItemsToTicket: async (
    { dispatch, commit, getters },
    data: {
      processedItems: [];
    },
  ): Promise<void> => {
    // Add the order items to the ticket
    commit('storeOrderItems', data.processedItems);
  },
  reopenCurrentTicket: async ({ dispatch, commit, getters }): Promise<void> => {
    const apiRoute = `${bpcRouterHelper.getEntityPath('invoice')}/pos/${
      getters.currentTicketId
    }/reopen-ticket`;
    const res = await bpcApi.put(apiRoute, {}).toPromise();
    const currentTicket = res.data;

    // Get open tickets and set the current one.
    await dispatch('getOpenTickets');
    commit('storeCurrentTicketId', currentTicket.id);
  },
};
const mutations = {
  storeOpenPosSession(state, data: PosSession) {
    console.log('storeOpenPosSession res', data);

    state.openPosSession = data;
  },
  clearOpenPosSession(state) {
    state.openPosSession = null;
    state.currentTicket = null;
    state.openTickets = [];
  },
  toggleDrawer(state) {
    state.showDrawer = !state.showDrawer;
    if (!state.showDrawer) {
      state.posExpanded = false;
      state.posInSidepane = false;
    } else {
      state.posExpanded = state.openTickets.length > 0;
    }
  },
  showDrawer(state) {
    state.showDrawer = true;
  },
  closeDrawer(state) {
    state.showDrawer = false;
    state.posExpanded = false;
    state.posInSidepane = false;
  },

  stopLoading(state) {
    state.loading = false;
  },
  startLoading(state) {
    state.loading = true;
  },
  stopLoadingTickets(state) {
    state.loadingTickets = false;
  },
  startLoadingTickets(state) {
    state.loadingTickets = true;
  },
  stopLoadingCurrentTicket(state) {
    state.loadingCurrentTicket = false;
  },
  startLoadingCurrentTicket(state) {
    state.loadingCurrentTicket = true;
  },
  stopChangingOrdersOfTicket(state) {
    state.changingOrdersOfTicket = false;
  },
  startChangingOrdersOfTicket(state) {
    state.changingOrdersOfTicket = true;
  },
  storeCurrentTicketId(state, data: number) {
    state.currentTicketId = data;
  },
  storeOrderItems(state, data: []) {
    state.orderItems = data;
  },
  storeOrderItemsToProcess(state, data: []) {
    state.orderItemsToProcess = data;
  },
  clearCurrentTicket(state) {
    state.currentTicketId = null;
    state.orderItems = [];
    state.orderItemsToProcess = [];
  },
  storeOpenTickets(state, data: Invoice[]) {
    state.openTickets = data;
  },
  replaceCurrentTicket(state, data: Invoice) {
    const newOpenTickets = state.openTickets.map(ticket => {
      if (ticket.id === data.id) {
        return data;
      } else {
        return ticket;
      }
    });
    state.openTickets = newOpenTickets;
  },
  clearOpenTickets(state) {
    state.openTickets = [];
  },

  togglePosExpanded(state) {
    state.posExpanded = !state.posExpanded;
    if (state.posExpanded) {
      state.posInSidepane = false;
    }
  },
  togglePosInSidepane(state) {
    state.posInSidepane = !state.posInSidepane;
    if (state.posInSidepane) {
      state.posExpanded = false;
    }
  },
  toggleResumeSessionDrawer(state) {
    state.showResumeSessionDrawer = !state.showResumeSessionDrawer;
  },
  toggleCloseSessionDrawer(state) {
    state.showCloseSessionDrawer = !state.showCloseSessionDrawer;
  },
  toggleModalsOpened(state) {
    state.modalsOpened = !state.modalsOpened;
  },
  storeArticleToAdd(state, data: Article) {
    state.articleToAdd = data;
  },
  storeNewTicketToOpenTickets(state, data: Invoice) {
    state.openTickets.push(data);
  },
  changeIsPrinting(state, val: boolean) {
    state.isPrinting = val;
  },
};

export const PosStoreModule = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
